import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  Box,
  Collapse,
  IconButton,
  MenuItem,
  TextField,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import { KeyboardArrowDown } from '@material-ui/icons';

import { getReasonCodes } from 'reducers/reasonCodesDB';
import { REASONS } from 'constants/reasonCodesDB';
import UIButton from 'components/UIElements/UIButton';
import ReasonCodesInput from 'components/inputs/ReasonCodesInput';

type SelectReturnReasonsProps = {
  globalReturnReason: string | undefined;
  applyCodeChange: () => void;
  changeRowsReturnReason: (str: string) => void;
  globalReasonRule: string;
  setGlobalReasonRule: (str: 'all' | 'unassigned') => void;
  hideReturnReasons: boolean;
};

const SetBulkReturnReasons: FC<SelectReturnReasonsProps> = ({
  globalReturnReason = '',
  changeRowsReturnReason,
  globalReasonRule,
  setGlobalReasonRule,
  applyCodeChange,
  hideReturnReasons = false,
}) => {
  const { t } = useTranslation('return', { keyPrefix: 'options' });
  const isMobileView = !useMediaQuery('(min-width:480px)');
  const returnCodeReasons = useSelector(getReasonCodes(REASONS.RETURN));

  const [open, setOpen] = useState(false);

  if (returnCodeReasons.length <= 0 || hideReturnReasons) {
    return null;
  }

  if (isMobileView) {
    return (
      <Collapse in={open} collapsedSize="60px">
        <Box padding="1rem 0">
          <Box
            display="flex"
            gridGap="1rem"
            alignItems="center"
            marginBottom="1rem"
          >
            <Typography>{t('setReason')}</Typography>
            <TextField
              select
              size="small"
              variant="outlined"
              onChange={e =>
                setGlobalReasonRule(e.target.value as 'all' | 'unassigned')
              }
              value={globalReasonRule}
              style={{ flexGrow: 1 }}
            >
              <MenuItem value="unassigned">{t('forUnassigned')}</MenuItem>
              <MenuItem value="all">{t('forAll')}</MenuItem>
            </TextField>
            <IconButton onClick={() => setOpen(prev => !prev)} size="small">
              <KeyboardArrowDown />
            </IconButton>
          </Box>
          <Box display="flex" gridGap="1rem" alignItems="center">
            <ReasonCodesInput
              onChange={e => changeRowsReturnReason(e.target.value)}
              reasonCodes={returnCodeReasons}
              value={globalReturnReason || 0}
              size="small"
            />
            <UIButton text={t('apply')} action={applyCodeChange} />
          </Box>
        </Box>
      </Collapse>
    );
  }

  return (
    <Box display="flex" gridGap="1rem" alignItems="center" padding="1rem">
      <Typography>{t('setReason')}</Typography>
      <TextField
        select
        fullWidth
        variant="outlined"
        onChange={e =>
          setGlobalReasonRule(e.target.value as 'all' | 'unassigned')
        }
        value={globalReasonRule}
      >
        <MenuItem value="unassigned">{t('forUnassigned')}</MenuItem>
        <MenuItem value="all">{t('forAll')}</MenuItem>
      </TextField>
      <ReasonCodesInput
        onChange={e => changeRowsReturnReason(e.target.value)}
        reasonCodes={returnCodeReasons}
        value={globalReturnReason || 0}
      />
      <UIButton text={t('apply')} action={applyCodeChange} />
    </Box>
  );
};

export default SetBulkReturnReasons;
